import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, MessageCircle, CalendarCheck, Guitar, BadgeCheck, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import RainbowArc from "@/components/RainbowArc";
import Stars from "@/components/Stars";
import CourseCard from "@/components/CourseCard";
import SkillModal from "@/components/SkillModal";
import PricingPlans from "@/components/PricingPlans";
import LeadForm from "@/components/LeadForm";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import { DriveImage } from "@/components/Drive";
import { COURSES, TESTIMONIALS, STORY_PICTURES, FAQS, IMG, LIFE } from "@/data/content";
import { SITE, waLink, PLAY_FIRST_WA, SMALL_PRINT } from "@/config/site";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

function Hero({ delayOffset = 0 }) {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 500], [0, 60]);
  const yCard = useTransform(scrollY, [0, 500], [0, -40]);

  return (
    <section ref={ref} className="relative pt-28 lg:pt-32 pb-16 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <h1 className="font-body font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]" data-testid="hero-heading">
            <MaskedLine delay={delayOffset + 0.03}>Play First,</MaskedLine>
            <MaskedLine delay={delayOffset + 0.12}><span className="text-brand italic">Pay Later.</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: delayOffset + 0.2 }}
            className="mt-6 text-base sm:text-lg text-muted leading-relaxed max-w-xl"
            data-testid="hero-subtext"
          >
            Play <span className="text-brand font-bold">2 trial sessions free</span>. Pay only if you love it. Zero risk.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: delayOffset + 0.3 }}
            className="mt-6 flex flex-wrap gap-2.5"
            data-testid="hero-steps"
          >
            {[
              { icon: CalendarCheck, label: "Book" },
              { icon: Guitar, label: "Play" },
              { icon: BadgeCheck, label: "Choose your membership" },
            ].map((s, i) => (
              <span key={s.label} className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 text-xs font-bold text-ink">
                <span className="text-brand font-extrabold">{i + 1}</span> <s.icon size={13} className="text-brand" /> {s.label}
              </span>
            ))}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: delayOffset + 0.4 }}
            className="mt-5 sm:mt-7 flex flex-wrap gap-2 sm:gap-3"
          >
            <Link
              to="/play-first-pay-later"
              data-testid="hero-cta-fitness-check"
              className="inline-flex items-center gap-2 rounded-full bg-brand text-white font-semibold text-sm sm:text-base px-5 py-3 sm:px-7 sm:py-3.5 hover:bg-ink transition-colors duration-300"
            >
              Book Your 2 Trial Sessions
            </Link>
            <a
              href={waLink(PLAY_FIRST_WA)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink font-semibold text-sm sm:text-base px-5 py-3 sm:px-7 sm:py-3.5 hover:border-ink hover:bg-ink hover:text-paper transition-colors duration-300"
            >
              <MessageCircle size={15} /> Book on WhatsApp
            </a>
            <a
              href={`tel:+91${SITE.whatsapp.slice(2)}`}
              data-testid="hero-cta-call"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink font-semibold text-sm sm:text-base px-5 py-3 sm:px-7 sm:py-3.5 hover:border-ink hover:bg-ink hover:text-paper transition-colors duration-300"
            >
              <Phone size={15} /> Call {SITE.phoneDisplay}
            </a>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: delayOffset + 0.5 }}
            className="mt-4 text-[11px] text-muted max-w-xl"
            data-testid="hero-small-print"
          >
            {SMALL_PRINT}
          </motion.p>
        </div>
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: delayOffset + 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[2rem] overflow-hidden border border-ink/10"
          >
            <motion.img
              src={IMG.hero}
              alt="Music lesson in progress at The Music Highway school of music, Sion East, Mumbai"
              style={{ y: yImg, scale: 1.15 }}
              className="w-full h-[420px] sm:h-[520px] object-cover"
              data-testid="hero-image"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/30 to-transparent" />
          </motion.div>
          <motion.div style={{ y: yCard }} className="absolute -bottom-6 right-3 sm:-right-4 animate-float-soft">
            <Link
              to="/play-first-pay-later"
              data-testid="hero-floating-card"
              className="relative block bg-white rounded-2xl border border-ink/10 shadow-[0_20px_50px_-20px_rgba(27,42,74,0.4)] p-4 pr-5 hover:border-brand/40 transition-colors duration-300"
            >
              <span className="absolute -top-2.5 right-4 bg-brand text-white text-[9px] font-bold tracking-[0.12em] px-2.5 py-0.5 rounded-full" data-testid="floating-card-tag">
                NEW STUDENTS
              </span>
              <p className="font-body font-bold text-sm text-ink leading-snug">
                Play First, Pay Later - <span className="text-brand font-extrabold text-lg">2 TRIAL</span> sessions
              </p>
              <p className="text-xs mt-1.5 inline-block rounded-md bg-sun/50 px-1.5 py-0.5 -mx-1.5 font-bold text-ink" data-testid="floating-card-sub">
                No payment today. You pay <span className="text-brand font-extrabold">₹ 0</span>
              </p>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function LifeSection() {
  return (
    <section className="py-20 bg-sand/60 border-t border-ink/10" data-testid="life-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="text-xs tracking-[0.3em] text-brand font-bold">BEYOND THE CLASSROOM</p>
          <h2 className="mt-3 font-body font-extrabold text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight" data-testid="life-title">{LIFE.title}</h2>
          <p className="mt-2 text-brand font-semibold text-base sm:text-lg">{LIFE.subtitle}</p>
          <p className="mt-3 text-sm text-muted leading-relaxed">{LIFE.shortLine}</p>
          <Link
            to="/life"
            data-testid="life-read-more"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink text-paper font-semibold text-sm px-7 py-3 hover:bg-brand transition-colors duration-300"
          >
            Read more <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function PlayFirstBanner() {
  return (
    <section className="relative bg-ink text-paper overflow-hidden" data-testid="fitness-banner">
      <RainbowArc className="absolute -top-2 left-0 w-full h-24 opacity-60" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 relative">
        <Reveal>
          <p className="text-xs tracking-[0.3em] text-sun font-bold">SIGNATURE FIRST STEP</p>
          <h2 className="mt-4 font-body font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">
            Not sure where to begin? Play first, pay later.
          </h2>
          <p className="mt-5 text-paper/70 leading-relaxed max-w-2xl">
            Take 2 free trial sessions with us at our Sion Studio. Continue with a membership if it’s the right fit, and owe nothing if it isn’t.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/play-first-pay-later" data-testid="fitness-banner-cta" className="inline-flex items-center gap-2 rounded-full bg-brand text-white font-semibold text-sm px-7 py-3.5 hover:bg-sun hover:text-ink transition-colors duration-300">
              Book Your 2 Trial Sessions <ArrowRight size={15} />
            </Link>
            <a href={waLink(PLAY_FIRST_WA)} target="_blank" rel="noopener noreferrer" data-testid="fitness-banner-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-paper/25 text-paper font-semibold text-sm px-7 py-3.5 hover:bg-paper hover:text-ink transition-colors duration-300">
              <MessageCircle size={15} /> Book on WhatsApp
            </a>
          </div>
          <p className="mt-5 text-[11px] text-paper/50 max-w-xl">{SMALL_PRINT}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="fitness-points-grid">
            {["Any Skill", "Instrument provided", "Real teacher, real sessions", "No charge if not for you"].map((item, i) => (
              <div key={item} className="rounded-2xl border border-paper/15 bg-paper/5 p-5" data-testid={`fitness-check-point-${i}`}>
                <span className="text-[10px] tracking-[0.2em] text-sun font-bold">0{i + 1}</span>
                <p className="font-body font-bold text-base mt-2">{item}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FaqLead() {
  return (
    <section className="py-20 bg-sand/60" data-testid="faq-lead-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <Reveal>
            <p className="text-xs tracking-[0.3em] text-brand font-bold">QUESTIONS</p>
            <h2 className="mt-3 font-body font-extrabold text-2xl sm:text-3xl text-ink tracking-tight">Before you ask</h2>
          </Reveal>
          <div className="mt-8 space-y-5">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.08}>
                <div className="border-b border-dashed border-ink/15 pb-5" data-testid={`faq-item-${i}`}>
                  <h3 className="font-body font-bold text-lg text-ink">{f.q}</h3>
                  <p className="text-sm text-muted leading-relaxed mt-2">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.1}>
          <div className="bg-white rounded-3xl border border-ink/10 p-7 sm:p-9 lg:sticky lg:top-24">
            <p className="text-xs tracking-[0.3em] text-brand font-bold">CALLBACK</p>
            <h3 className="mt-3 font-body font-extrabold text-2xl text-ink tracking-tight">Prefer we call you?</h3>
            <p className="text-sm text-muted mt-2 mb-6">Drop your details and The Music Highway team will call you back to schedule your first session.</p>
            <LeadForm source="home-callback" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const [activeSkill, setActiveSkill] = useState(null);
  return (
    <main data-testid="home-page">
      <Seo
        title="Music Classes in Sion, Mumbai | The Music Highway"
        description="Guitar, Keyboard, Violin, Flute, Singing & Composition classes at The Music Highway, Sion East, Mumbai. Play first, pay later - start with 2 free trial sessions."
      />
      <Hero delayOffset={0} />
      <Marquee />
      <section className="py-20" data-testid="courses-preview-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-xs tracking-[0.3em] text-brand font-bold" data-testid="courses-eyebrow">PICK YOUR HIGHWAY</p>
              <h2 className="mt-3 font-body font-extrabold text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">Six Skills. One stage.</h2>
            </div>
            <Link to="/courses" data-testid="courses-preview-view-all" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-brand transition-colors">
              Full curriculum <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {COURSES.map((c, i) => (
              <CourseCard key={c.id} course={c} index={i} onView={(course) => setActiveSkill({ course, index: i })} />
            ))}
          </div>
        </div>
      </section>
      <PricingPlans />
      <LifeSection />
      <PlayFirstBanner />
      <section className="py-20" data-testid="stories-preview-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-xs tracking-[0.3em] text-brand font-bold">STUDENT FEEDBACK</p>
              <h2 className="mt-3 font-body font-extrabold text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">What our community says about us.</h2>
            </div>
            <Link to="/testimonials" data-testid="stories-preview-view-all" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-brand transition-colors">
              All stories <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-5">
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <figure className="bg-white rounded-2xl border border-ink/10 overflow-hidden h-full" data-testid={`story-card-${i}`}>
                  <DriveImage
                    link={STORY_PICTURES[i]}
                    alt={`Photo of ${t.name}`}
                    width={640}
                    height={480}
                    className="w-full aspect-[4/3] overflow-hidden"
                    imgClassName="w-full h-full"
                  />
                  <div className="p-6">
                    <Stars rating={t.rating} showValue />
                    <blockquote className="text-sm text-ink/80 leading-relaxed mt-3">“{t.quote}”</blockquote>
                    <figcaption className="mt-5 pt-4 border-t border-dashed border-ink/15 flex items-center gap-3">
                      <DriveImage
                        link={STORY_PICTURES[i]}
                        alt={`Photo of ${t.name}`}
                        width={96}
                        height={96}
                        className="w-11 h-11 rounded-full overflow-hidden border-2 border-brand/40 shrink-0"
                        imgClassName="w-full h-full"
                      />
                      <div>
                        <p className="font-body font-bold text-sm text-ink">{t.name}</p>
                        <p className="text-[10px] tracking-[0.15em] text-muted mt-0.5">{t.meta.toUpperCase()}</p>
                      </div>
                    </figcaption>
                  </div>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <FaqLead />
      <section className="py-16 border-t border-ink/10" data-testid="studio-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <Reveal>
            <p className="text-xs tracking-[0.3em] text-brand font-bold">THE STUDIO</p>
            <p className="mt-3 font-body font-bold text-xl text-ink flex gap-2 items-start">
              <MapPin size={18} className="text-brand mt-1 shrink-0" /> {SITE.address}
            </p>
            <p className="text-xs text-muted mt-2" data-testid="studio-timings">{SITE.timings}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/contact" data-testid="studio-strip-cta" className="inline-flex items-center gap-2 rounded-full bg-ink text-paper font-semibold text-sm px-7 py-3.5 hover:bg-brand transition-colors duration-300">
              Get directions <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
      <section className="py-16 sm:py-20 bg-sand/60 border-t border-ink/10" data-testid="home-trial-form-section">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center">
            <p className="text-xs tracking-[0.3em] text-brand font-bold">2 FREE TRIAL CLASSES</p>
            <h2 className="mt-3 font-body font-extrabold text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight" data-testid="home-trial-form-heading">
              Start Your <span className="text-brand italic">Free Trial</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-muted">Play first, pay later. No commitment.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 bg-white rounded-3xl border border-ink/10 p-6 sm:p-8 shadow-[0_24px_60px_-40px_rgba(27,42,74,0.3)]">
              <LeadCaptureForm source="home-embedded" />
            </div>
          </Reveal>
        </div>
      </section>
      <SkillModal course={activeSkill?.course} index={activeSkill?.index || 0} onClose={() => setActiveSkill(null)} />
    </main>
  );
}
